import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Truck,
  Award,
  Clock,
  Users,
  Phone,
  Mail,
  MapPin,
  Settings,
  Hammer,
  Building,
  HardHat,
  Wrench,
  CheckCircle,
  Shield,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";

// Import images
import asphaltPavingDriveway from "@/assets/asphalt-paving-driveway.jpg";
import commercialParkingLotPaving from "@/assets/commercial-parking-lot-paving.jpg";
import roadResurfacingRepair from "@/assets/road-resurfacing-repair.jpg";
import asphaltSealcoating from "@/assets/asphalt-sealcoating.jpg";

const AsphaltPavingServices = () => {
  const faqData = [
    {
      question: "What types of asphalt paving services do you provide?",
      answer:
        "We provide comprehensive asphalt paving services including new driveway installation, parking lot construction, road resurfacing, pothole repair, crack sealing, sealcoating, and line striping for residential and commercial properties.",
    },
    {
      question: "How long does asphalt paving last?",
      answer:
        "Properly installed and maintained asphalt can last 15-25 years for driveways and 20-30 years for parking lots. Regular maintenance like sealcoating every 3-5 years significantly extends the lifespan.",
    },
    {
      question: "What's the difference between asphalt and concrete paving?",
      answer:
        "Asphalt is more flexible, cost-effective, and easier to repair than concrete. It's ideal for driveways and parking areas, while concrete is better for heavy-duty applications and decorative surfaces.",
    },
    {
      question: "When is the best time for asphalt paving in Ontario?",
      answer:
        "The best time for asphalt paving in Ontario is during warm, dry weather from late spring to early fall (May to October) when temperatures are consistently above 10°C.",
    },
    {
      question: "Do you provide asphalt maintenance services?",
      answer:
        "Yes, we offer complete asphalt maintenance including crack sealing, pothole repair, sealcoating, line striping, and resurfacing to keep your pavement in excellent condition.",
    },
    {
      question: "Can you repair existing asphalt driveways and parking lots?",
      answer:
        "Absolutely. We provide comprehensive asphalt repair services including pothole filling, crack repair, edge restoration, drainage improvements, and partial or full resurfacing as needed.",
    },
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Asphalt Paving Services Niagara Region, Hamilton, Toronto Ontario",
    description:
      "Professional asphalt paving and maintenance services including driveways, parking lots, road resurfacing, Niagara Region, Hamilton, Toronto Ontario.",
    provider: {
      "@type": "LocalBusiness",
      name: "Blue Knight Contracting",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Ontario",
        addressCountry: "CA",
      },
      telephone: "+1-647-760-8854",
    },
    areaServed: "Ontario, Canada",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Asphalt Paving Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Driveway Paving",
            description: "Professional asphalt driveway installation and repair",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Parking Lot Construction",
            description: "Commercial parking lot paving and maintenance",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Asphalt Maintenance",
            description: "Sealcoating, crack repair, and resurfacing services",
          },
        },
      ],
    },
  };

  const pavingServices = [
    {
      title: "Driveway Paving",
      description: "Professional asphalt driveway installation, repair, and resurfacing for residential properties.",
      icon: Truck,
      services: [
        "New driveway installation",
        "Driveway resurfacing",
        "Driveway repair",
        "Driveway extensions",
        "Proper drainage systems",
        "Edge restoration",
      ],
    },
    {
      title: "Parking Lot Construction",
      description: "Commercial parking lot paving, striping, and maintenance for businesses and institutions.",
      icon: Building,
      services: [
        "New parking lot construction",
        "Parking lot resurfacing",
        "Line striping & marking",
        "ADA compliance updates",
        "Traffic flow optimization",
        "Handicap accessibility",
      ],
    },
    {
      title: "Road Resurfacing",
      description: "Municipal and private road resurfacing, repair, and maintenance services.",
      icon: Settings,
      services: [
        "Road resurfacing",
        "Pothole repair",
        "Crack sealing",
        "Edge repairs",
        "Drainage improvements",
        "Traffic management",
      ],
    },
    {
      title: "Asphalt Maintenance",
      description: "Comprehensive maintenance services to extend the life of your asphalt surfaces.",
      icon: Wrench,
      services: [
        "Sealcoating application",
        "Crack filling & sealing",
        "Pothole repair",
        "Surface cleaning",
        "Preventive maintenance",
        "Annual inspections",
      ],
    },
    {
      title: "Commercial Paving",
      description: "Large-scale commercial and industrial asphalt paving projects.",
      icon: HardHat,
      services: [
        "Industrial site paving",
        "Loading dock areas",
        "Warehouse access roads",
        "Heavy-duty pavements",
        "Equipment staging areas",
        "Site preparation",
      ],
    },
    {
      title: "Repair & Restoration",
      description: "Professional repair and restoration of damaged asphalt surfaces.",
      icon: Hammer,
      services: [
        "Full-depth repairs",
        "Surface patching",
        "Structural repairs",
        "Base reconstruction",
        "Drainage solutions",
        "Emergency repairs",
      ],
    },
  ];

  const serviceAreas = [
    "Toronto",
    "Ottawa",
    "Hamilton",
    "London",
    "Windsor",
    "Kitchener-Waterloo",
    "Thunder Bay",
    "Sudbury",
    "Kingston",
    "Barrie",
    "Oshawa",
    "St. Catharines",
    "Cambridge",
    "Guelph",
    "Brantford",
    "Peterborough",
    "Sarnia",
    "Belleville",
  ];

  return (
    <>
      <SEOMeta 
        title="Asphalt Paving Ontario | Driveways, Parking Lots & Road Repair | Niagara Region, Hamilton and Toronto GTA"
        description="Professional asphalt paving services in Ontario. Driveways, parking lots, road resurfacing, sealcoating & repairs. Serving Toronto, Hamilton, Niagara & GTA."
        keywords="asphalt paving Ontario, driveway paving Toronto, parking lot paving, asphalt repair Hamilton, sealcoating Niagara, road resurfacing GTA, asphalt contractor, Niagara Region asphalt paving, Hamilton asphalt paving, Toronto GTA asphalt paving"
        canonicalUrl="https://blueknightcontracting.ca/services/asphalt-paving"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />

      <Header />

      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Professional Asphalt Paving
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  Asphalt Paving Services Across Ontario
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  Professional asphalt paving, maintenance, and repair services for driveways, parking lots, and roads.
                  Quality materials, expert installation, and long-lasting results throughout Ontario.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Paving Quote</Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20"
                    asChild
                  >
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    <span>Quality Materials</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>Fast Installation</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src={asphaltPavingDriveway}
                  alt="Professional asphalt paving services in Ontario"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Our Paving Services
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Comprehensive Asphalt Paving Solutions</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From residential driveways to commercial parking lots, we provide complete asphalt paving and
                maintenance services with quality materials and expert workmanship.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pavingServices.map((service, index) => (
                <Card
                  key={index}
                  className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm"
                >
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Recent Projects
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Professional Asphalt Paving Projects</h2>
              <p className="text-xl text-muted-foreground">
                See our asphalt paving expertise in action across various residential and commercial projects.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img
                    src={asphaltPavingDriveway}
                    alt="Professional driveway paving"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Residential Driveway</h3>
                    <p className="text-sm text-white/80">Quality installation</p>
                  </div>
                </div>
              </Card>

              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img
                    src={commercialParkingLotPaving}
                    alt="Commercial parking lot paving"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Commercial Parking Lot</h3>
                    <p className="text-sm text-white/80">Professional paving</p>
                  </div>
                </div>
              </Card>

              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img
                    src={roadResurfacingRepair}
                    alt="Road resurfacing and repair"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Road Resurfacing</h3>
                    <p className="text-sm text-white/80">Municipal projects</p>
                  </div>
                </div>
              </Card>

              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img
                    src={asphaltSealcoating}
                    alt="Asphalt sealcoating service"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Sealcoating Service</h3>
                    <p className="text-sm text-white/80">Protective maintenance</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Quality Materials CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Award className="h-8 w-8" />
                <h2 className="text-3xl font-bold">Quality Materials & Expert Installation</h2>
              </div>
              <p className="text-xl mb-8 opacity-90">
                We use only premium asphalt materials and proven installation techniques for long-lasting, durable
                results that stand up to Ontario's weather conditions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Request Free Estimate</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20"
                  asChild
                >
                  <a href="tel:+16477608854">Call for Quote: (647) 760-8854</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Why Choose Blue Knight Paving
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ontario's Trusted Asphalt Paving Specialists</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional installation, quality materials, and proven expertise make us the preferred choice for
                asphalt paving projects across Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium asphalt mixes and proven materials for long-lasting results.
                </p>
              </Card>

              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Expert Crews</h3>
                <p className="text-muted-foreground">
                  Experienced paving professionals with specialized equipment and training.
                </p>
              </Card>

              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fast Installation</h3>
                <p className="text-muted-foreground">
                  Efficient project completion with minimal disruption to your schedule.
                </p>
              </Card>

              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Warranty Protection</h3>
                <p className="text-muted-foreground">
                  Comprehensive warranties on workmanship and materials for peace of mind.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Service Coverage
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Asphalt Paving Services Across Ontario</h2>
              <p className="text-xl text-muted-foreground">
                We provide professional asphalt paving services throughout Ontario's major cities and communities.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {serviceAreas.map((area, index) => (
                  <Card
                    key={index}
                    className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Frequently Asked Questions
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Asphalt Paving Services FAQ</h2>
              <p className="text-xl text-muted-foreground">
                Common questions about our asphalt paving, maintenance, and repair services.
              </p>
            </div>

            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready for Professional Asphalt Paving?</h2>
              <p className="text-xl mb-8 text-white/90">
                Get expert asphalt paving services with quality materials and professional installation. Contact us for
                a free estimate and competitive pricing.
              </p>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>

                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>

                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">All of Ontario</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Free Paving Estimate</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20"
                  asChild
                >
                  <Link to="/gallery">View Our Paving Work</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AsphaltPavingServices;
